import * as React from "react";
import { Modal, Button, Segment, Header, Icon, Dimmer, Loader, Message, Menu, Checkbox, Label } from "semantic-ui-react";
import AceEditor from "react-ace";
import "ace-builds/src-noconflict/mode-text";
import "ace-builds/src-noconflict/theme-monokai";

const MAX_FILES = 5;

interface FileListItem {
    id: string;
    file: File;
    name: string;
    extractedText: string;
    previewUrl: string;
    previewType: 'image' | 'pdf' | 'none';
    duration: number;
    isProcessed: boolean;
    isProcessing: boolean;
    processingError?: string;
    isSelectedForApply: boolean;
}

interface FileUploadModalProps {
    isOpen: boolean;
    onClose: () => void;
    onApply: (text: string) => void;
}

export function FileUploadModal({ isOpen, onClose, onApply }: FileUploadModalProps) {
    // --- State ---
    const [fileList, setFileList] = React.useState<FileListItem[]>([]);
    const [activeFileId, setActiveFileId] = React.useState<string | null>(null);
    const [globalLoading, setGlobalLoading] = React.useState<boolean>(false);
    const [globalError, setGlobalError] = React.useState<string>("");
    const [isDragging, setIsDragging] = React.useState<boolean>(false);

    // Merged Preview Modal State
    const [showMergedPreview, setShowMergedPreview] = React.useState<boolean>(false);
    const [mergedPreviewText, setMergedPreviewText] = React.useState<string>("");

    const fileInputRef = React.useRef<HTMLInputElement | null>(null);
    const aceEditorRef = React.useRef<any>(null);

    // --- Computed ---
    const activeFile = React.useMemo(() =>
            fileList.find(f => f.id === activeFileId),
        [fileList, activeFileId]);

    const selectedFiles = fileList.filter(f => f.isSelectedForApply && f.isProcessed && !f.processingError);
    const selectedCount = selectedFiles.length;

    React.useEffect(() => {
        if (!isOpen) {
            fileList.forEach(f => {
                if (f.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(f.previewUrl);
            });
            setFileList([]);
            setActiveFileId(null);
            setGlobalError("");
            setIsDragging(false);
        }
    }, [isOpen]);

    const generateId = () => `file_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const isImage = (name: string) => /\.(jpe?g|png)$/i.test(name);
    const isPdf = (name: string) => /\.pdf$/i.test(name);
    const needsPdfConversion = (name: string) => /\.(eml|esg|mail|jsonmail|html?|xlsx|xls)$/i.test(name);

    const addFilesToList = (files: File[]) => {
        setGlobalError("");
        if (files.length === 0) return;

        setFileList(prev => {
            const availableSlots = MAX_FILES - prev.length;
            if (availableSlots <= 0) {
                setGlobalError(`Cannot add more files. Limit is ${MAX_FILES}.`);
                return prev;
            }

            const filesToAdd = files.slice(0, availableSlots);
            const newItems: FileListItem[] = filesToAdd.map(file => ({
                id: generateId(),
                file,
                name: file.name,
                extractedText: "",
                previewUrl: "",
                previewType: 'none',
                duration: 0,
                isProcessed: false,
                isProcessing: false,
                isSelectedForApply: false,
            }));

            // Auto-activate first new file if none active
            if (!activeFileId && newItems.length > 0) {
                setActiveFileId(newItems[0].id);
            }
            return [...prev, ...newItems];
        });
    };

    const removeFile = (id: string) => {
        setFileList(prev => {
            const file = prev.find(f => f.id === id);
            if (file?.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(file.previewUrl);

            const remaining = prev.filter(f => f.id !== id);
            if (activeFileId === id) setActiveFileId(remaining[0]?.id || null);
            return remaining;
        });
    };

    const handleDragEnter = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if(!globalLoading) setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        if (globalLoading) return;

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            addFilesToList(Array.from(e.dataTransfer.files));
        }
    };

    React.useEffect(() => {
        const handlePaste = (e: ClipboardEvent) => {
            if (!isOpen || globalLoading) return;

            // Ignore if pasting text into an input or the Ace Editor
            const target = e.target as HTMLElement;
            if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.closest('.ace_editor')) {
                return;
            }

            const items = e.clipboardData?.items;
            if (!items) return;

            const files: File[] = [];
            for (let i = 0; i < items.length; i++) {
                if (items[i].kind === 'file') {
                    const file = items[i].getAsFile();
                    if (file) files.push(file);
                }
            }

            if (files.length > 0) {
                e.preventDefault();
                addFilesToList(files);
            }
        };

        window.addEventListener('paste', handlePaste);
        return () => window.removeEventListener('paste', handlePaste);
    }, [isOpen, globalLoading]);

    const fileToBase64 = (file: File): Promise<string> => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
    });

    const processFileItem = async (item: FileListItem): Promise<Partial<FileListItem>> => {
        try {
            const base64Full = await fileToBase64(item.file);
            const base64Content = base64Full.split(",")[1];

            const textResponse = await fetch("/converter/createInputText", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ base64Data: base64Content, fileName: item.name }),
            });

            if (!textResponse.ok) throw new Error("Text extraction failed");

            const textData = await textResponse.json();
            const extractedText = textData.inputText || "";
            const duration = textData.durationMS ? (textData.durationMS / 1000) : 0;

            // 2. Preview Generation
            let previewUrl = "";
            let previewType: 'image' | 'pdf' | 'none' = 'none';

            if (isImage(item.name)) {
                previewUrl = base64Full;
                previewType = 'image';
            } else if (isPdf(item.name)) {
                previewUrl = URL.createObjectURL(item.file);
                previewType = 'pdf';
            } else if (needsPdfConversion(item.name)) {
                const pdfResponse = await fetch("/converter/fileToPdf", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ base64Data: base64Content, fileName: item.name }),
                });

                if (pdfResponse.ok) {
                    const blob = await pdfResponse.blob();
                    previewUrl = URL.createObjectURL(blob);
                    previewType = 'pdf';
                }
            }

            return {
                isProcessed: true,
                isProcessing: false,
                extractedText,
                duration,
                previewUrl,
                previewType,
                processingError: undefined,
                isSelectedForApply: true
            };

        } catch (error: any) {
            console.error(error);
            return {
                isProcessed: true,
                isProcessing: false,
                processingError: error.message || "Processing Failed",
                extractedText: "",
                isSelectedForApply: false
            };
        }
    };

    const handleProcessActive = async () => {
        if (!activeFileId) return;
        setGlobalLoading(true);

        const currentFile = fileList.find(f => f.id === activeFileId);
        if (currentFile) {
            setFileList(prev => prev.map(f => f.id === activeFileId ? { ...f, isProcessing: true, processingError: undefined } : f));
            const result = await processFileItem(currentFile);
            setFileList(prev => prev.map(f => f.id === activeFileId ? { ...f, ...result } : f));
        }

        setGlobalLoading(false);
    };

    const handleProcessAll = async () => {
        setGlobalLoading(true);
        const toProcess = fileList.filter(f => !f.isProcessed);

        setFileList(prev => prev.map(f => toProcess.some(tp => tp.id === f.id) ? { ...f, isProcessing: true, processingError: undefined } : f));

        for (const item of toProcess) {
            const result = await processFileItem(item);
            setFileList(prev => prev.map(f => f.id === item.id ? { ...f, ...result } : f));
        }
        setGlobalLoading(false);
    };

    const handleApply = () => {
        // If files are selected, merge them. If not, but active file is valid, use that.
        const filesToUse = selectedCount > 0
            ? selectedFiles
            : (activeFile && activeFile.isProcessed && !activeFile.processingError) ? [activeFile] : [];

        if (filesToUse.length > 0) {
            const text = filesToUse.map(f => f.extractedText).join("\n\n---\n\n");
            onApply(text);
            onClose();
        } else {
            setGlobalError("No valid processed files selected.");
        }
    };

    const handleShowMergedPreview = () => {
        if (selectedCount > 1) {
            const text = selectedFiles.map(f => f.extractedText).join("\n\n---\n\n");
            setMergedPreviewText(text);
            setShowMergedPreview(true);
        }
    };

    React.useEffect(() => {
        if (activeFileId && aceEditorRef.current?.editor) {
            const resize = () => aceEditorRef.current.editor.resize();
            resize();
            setTimeout(resize, 50);
        }
    }, [activeFileId, isOpen]);

    return (
        <Modal open={isOpen} onClose={onClose} size="fullscreen" dimmer="blurring">
            <Modal.Header>Upload and Process Files</Modal.Header>
            <Modal.Content
                style={{ height: '85vh', display: 'flex', flexDirection: 'column', position: 'relative' }}
                onDragEnter={handleDragEnter}
                onDragOver={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                {/* Drag & Drop Overlay */}
                {isDragging && (
                    <Dimmer active simple style={{ zIndex: 1000, border: '3px dashed #2185d0', backgroundColor: 'rgba(255,255,255,0.9)' }}>
                        <Header as="h2" icon>
                            <Icon name="cloud upload" color="blue" />
                            Drop files here to add
                        </Header>
                    </Dimmer>
                )}

                {(globalLoading) && <Dimmer active inverted><Loader>Processing...</Loader></Dimmer>}

                {globalError && (
                    <Message negative onDismiss={() => setGlobalError("")} content={globalError} size="small" style={{flexShrink: 0}} />
                )}

                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', flexWrap: 'wrap', alignItems: 'center', flexShrink: 0 }}>
                    <Button.Group size="small">
                        <Button icon="file alternate outline" content="Add Files" onClick={() => fileInputRef.current?.click()} disabled={globalLoading} />

                        <Button
                            primary
                            icon={activeFile?.isProcessed ? "refresh" : "cogs"}
                            content={activeFile ? `Process "${activeFile.name.length > 15 ? activeFile.name.substr(0,12)+'...' : activeFile.name}"` : "Process Active"}
                            onClick={handleProcessActive}
                            disabled={globalLoading || !activeFile}
                            title="Process the currently viewed file"
                        />

                        <Button
                            color="teal"
                            icon="tasks"
                            content="Process All"
                            onClick={handleProcessAll}
                            disabled={globalLoading || fileList.length === 0}
                        />
                    </Button.Group>

                    {selectedCount > 1 && (
                        <Button
                            size="small"
                            color="blue"
                            icon="eye"
                            content={`Preview Merged (${selectedCount})`}
                            onClick={handleShowMergedPreview}
                        />
                    )}

                    <Label size="small" style={{ marginLeft: 'auto' }}>
                        <Icon name="keyboard" /> Paste (Ctrl+V) or Drag & Drop supported
                    </Label>

                    <input
                        type="file"
                        ref={fileInputRef}
                        style={{display:'none'}}
                        multiple
                        accept="image/*,.pdf,.doc,.docx,.txt,.rtf,.eml,.esg,.mail,.jsonmail,.html,.htm,.xlsx,.xls"
                        onChange={(e) => {
                            if(e.target.files) addFilesToList(Array.from(e.target.files));
                            e.target.value = '';
                        }}
                    />
                </div>

                {fileList.length > 0 ? (
                    <div style={{ flexShrink: 0 }}>
                        <Menu tabular size="small" attached="top" style={{
                            overflowX: 'auto',
                            flexWrap: 'nowrap',
                            display: 'flex'
                        }}
                        >
                            {fileList.map(f => (
                                <Menu.Item
                                    key={f.id}
                                    active={activeFileId === f.id}
                                    onClick={() => setActiveFileId(f.id)}
                                    style={{ padding: '0.6em 0.8em', minWidth: '120px' }}
                                >
                                    <Checkbox
                                        checked={f.isSelectedForApply}
                                        onClick={(e) => { e.stopPropagation(); }}
                                        onChange={() => setFileList(prev =>
                                            prev.map(item => item.id === f.id ?
                                                {...item, isSelectedForApply: !item.isSelectedForApply} : item))}
                                        disabled={!f.isProcessed || !!f.processingError}
                                        style={{ marginRight: '6px' }}
                                        title={f.isProcessed ? "Include in final result" : "Process file to select"}
                                    />

                                    {f.isProcessing && <Loader active inline size="mini" style={{marginRight: 5}}/>}

                                    {!f.isProcessing && f.isProcessed && !f.processingError && <Icon name="check circle" color="green" />}
                                    {!f.isProcessing && f.processingError && <Icon name="warning circle" color="red" />}

                                    <span style={{
                                        maxWidth: '150px',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        display:'inline-block',
                                        verticalAlign:'middle',
                                        fontWeight: activeFileId === f.id ? 'bold' : 'normal'
                                    }}>
                                        {f.name}
                                    </span>

                                    <Icon
                                        name="close"
                                        link
                                        onClick={(e) => { e.stopPropagation(); removeFile(f.id); }}
                                        style={{ marginLeft: 5, opacity: 0.6 }}
                                    />
                                </Menu.Item>
                            ))}
                        </Menu>
                    </div>
                ) : (
                    <Segment placeholder style={{ flexGrow: 1, border: '1px dashed #ccc' }}>
                        <Header icon>
                            <Icon name="file outline" />
                            No files selected
                            <Header.Subheader>Drag and drop files here or click "Add Files"</Header.Subheader>
                        </Header>
                    </Segment>
                )}

                {fileList.length > 0 && (
                    <Segment attached="bottom" style={{
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: 0,
                        padding: 0,
                        border: '1px solid #d4d4d5'
                    }}
                    >
                        {activeFile ? (
                            <div style={{ display: 'flex', flex: 1, height: '100%', overflow: 'hidden' }}>

                                <div style={{
                                    flex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    borderRight: '1px solid #ddd',
                                    minWidth: 0
                                }}
                                >
                                    <div style={{
                                        padding: '0.5rem',
                                        background: '#f8f8f8',
                                        borderBottom: '1px solid #ddd',
                                        display:'flex',
                                        justifyContent:'space-between',
                                        alignItems:'center',
                                        flexShrink: 0
                                    }}
                                    >
                                        <Header as="h5" style={{margin:0}}><Icon name="file text" /> Extracted Text</Header>

                                        {activeFile.isProcessed && !activeFile.processingError && (
                                            <Label size="tiny" color="green" basic>
                                                <Icon name="clock" /> {activeFile.duration.toFixed(2)}s
                                            </Label>
                                        )}
                                        {activeFile.processingError && <Label color="red" size="tiny">{activeFile.processingError}</Label>}
                                    </div>
                                    <div style={{ flex: 1, position: 'relative' }}>
                                        <AceEditor
                                            ref={aceEditorRef}
                                            mode="text"
                                            theme="monokai"
                                            value={activeFile.extractedText}
                                            onChange={(val) => {
                                                setFileList(prev => prev.map(f => f.id === activeFileId ? { ...f, extractedText: val } : f));
                                            }}
                                            width="100%"
                                            height="100%"
                                            name="active_editor_instance"
                                            fontSize={14}
                                            showPrintMargin={false}
                                            readOnly={activeFile.isProcessing}
                                        />
                                    </div>
                                </div>

                                <div style={{
                                    flex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    background: '#f3f3f3',
                                    minWidth: 0 }}>
                                    <div style={{ padding: '0.5rem', background: '#f8f8f8', borderBottom: '1px solid #ddd', flexShrink: 0 }}>
                                        <Header as="h5" style={{margin:0}}><Icon name="eye" /> Original Preview</Header>
                                    </div>
                                    <div style={{
                                        flex: 1,
                                        display:'flex',
                                        alignItems:'center',
                                        justifyContent:'center',
                                        overflow:'hidden',
                                        padding: '10px'
                                    }}
                                    >
                                        {activeFile.isProcessing ? (
                                            <Loader active inline size="large">Generating Preview...</Loader>
                                        ) : activeFile.previewUrl ? (
                                            activeFile.previewType === 'image' ? (
                                                <img src={activeFile.previewUrl} alt="Preview" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                                            ) : activeFile.previewType === 'pdf' ? (
                                                <iframe
                                                    src={`${activeFile.previewUrl}#view=FitH`}
                                                    width="100%"
                                                    height="100%"
                                                    style={{border:'none', background: 'white'}}
                                                    title="PDF Preview"
                                                />
                                            ) : (
                                                <div style={{color:'#666'}}>No visual preview available</div>
                                            )
                                        ) : (
                                            <div style={{color:'#999', textAlign:'center'}}>
                                                <Icon name={activeFile.isProcessed ? "file outline" : "arrow up"} size="big" />
                                                <p style={{marginTop: 10}}>{activeFile.isProcessed ?
                                                    "Preview not available for this file type" : "Process file to generate preview"}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100%', color: '#ccc' }}>
                                <div>
                                    <Icon name="hand point up outline" size="large" />
                                    <p>Select a file tab to view content</p>
                                </div>
                            </div>
                        )}
                    </Segment>
                )}

                <Modal open={showMergedPreview} onClose={() => setShowMergedPreview(false)} size="large">
                    <Modal.Header>Merged Output ({selectedCount} files)</Modal.Header>
                    <Modal.Content scrolling>
                        <Message info>
                            <Icon name="info circle" />
                            This is how the text will look when applied. You can apply directly from here.
                        </Message>
                        <Segment>
                            <pre style={{whiteSpace: 'pre-wrap', fontFamily: 'monospace', margin: 0}}>{mergedPreviewText}</pre>
                        </Segment>
                    </Modal.Content>
                    <Modal.Actions>
                        <Button onClick={() => setShowMergedPreview(false)}>Back</Button>
                        <Button primary onClick={() => { onApply(mergedPreviewText); setShowMergedPreview(false); onClose(); }}>
                            Apply Merged Text
                        </Button>
                    </Modal.Actions>
                </Modal>
            </Modal.Content>

            <Modal.Actions>
                <div style={{ float: 'left', display: 'flex', alignItems: 'center', height: '100%', color: '#666' }}>
                    <small>{selectedCount} file(s) selected for application</small>
                </div>
                <Button onClick={onClose} disabled={globalLoading}>Cancel</Button>
                <Button
                    primary
                    onClick={handleApply}
                    disabled={globalLoading || (selectedCount === 0 && (!activeFile || !activeFile.isProcessed))}
                >
                    Apply {selectedCount > 1 ? 'Merged' : 'Text'}
                </Button>
            </Modal.Actions>
        </Modal>
    );
}